import { Card, Row } from "antd"
import cx from "clsx"
import {
  AiFeedbackForm,
  Button,
  DataTable,
  DateFormat,
  Icon,
  notification,
  Number,
  Space,
  Text,
  Tooltip,
} from "components/common"
import { FLagWithText } from "components/common/flagWithText/flagWithText"
import { ActionButtons, IconTextDate, Separator, Sort, TitleDescription } from "components/custom"
import { SvgIconAi } from "components/svg"
import { TIME_DATE_FORMAT } from "constants"
import { CLIENT_DETAIL_PATH } from "constants/app-paths"
import dayjs from "dayjs"
import { CallButton } from "features/clients/components"
import WhatsappCallButton from "features/clients/components/whatsapp-call-button"
import { AddSalesModal } from "features/leads/components"
import { AddSalesContextProvider } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"
import CloseLostForm from "features/leads/components/closeLostForm/closeLostForm"
import ViewSharingDetail from "features/leads/components/viewSharingDetail/viewSharingDetail"
import { CallLogsDrawer, ProjectModal, TaskOverviewModal } from "features/shared/components"
import AddToSalesPipeline from "features/shared/components/addToSalesPipeline/addToSalesPipeline"
import EditIconForEditTask from "features/shared/components/editIconForEditTask/editIconForEditTask"
import EditedTaskShowPreviousButton from "features/shared/components/editedTaskShowPreviousButton/editedTaskShowPreviousButton"
import EditedTaskTooltip from "features/shared/components/editedTaskTooltip/editedTaskTooltip"
import UserProfileSummary from "features/shared/components/userProfileSummary"
import { classifications } from "features/shared/data"
import { useLazyGetEditedTaskDetailsQuery } from "features/tasks/api"
import { AddTaskForm } from "features/tasks/components"
import { useGetTodosQuery } from "features/todos/api"
import styles from "features/todos/components/todosTable/index.module.less"
import TodosFilters from "features/todos/listings/components/filters"
import { TODOS_CONFIG } from "features/todos/utils/constants"
import useManageExpandedRowIds from "hooks/useExpandedRowIds"
import { usePageTitle } from "hooks/usePageTitle"
import { t } from "i18next"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link, useSearchParams } from "react-router-dom"
import { capitalizeFirstLetter, convertToReadableString, joinBytItems, localisedValue, openMailTo } from "utils"
import { getElapsedTime } from "utils/date"
import { modifyUrlParamsForExistingSearches } from "utils/filters"
import { PAGE_SUBSECTIONS, PAGES } from "utils/gtm/gtmEventsData"
import getPrivilege from "utils/privileges/privileges"
import theme from "utils/themeVars"
import { configs } from "../../../../configs"
import GrandStreamCallButton from "../../../clients/components/grandStreamCallButton"

export default function TodosTable({
  from = { page: PAGES.client_listing_page },
  params = {},
  selectedTodos = [],
  setSelectedTodos = () => {},
  showRowSelection = false,
  getCount = () => {},
  handleIsFetching = () => {},
  extraProps = {},
  defaultParams = {},
}) {
  //props

  //refs
  const taskLogRef = useRef()
  const addTaskRef = useRef()
  const closeLostRef = useRef()
  const callDrawerRef = useRef()
  const aiFeedbackRef = useRef()
  const addToSalesPipelineRef = useRef()
  const projectRef = useRef()
  const addSalesFormRef = useRef()
  const filtersRef = useRef()

  //constants
  usePageTitle(t("Client Todos - Propforce"))
  const page_type = from.page
  const removeTabs = TODOS_CONFIG[page_type]?.removeTabs || false

  const items = [
    { sortOrder: "asc", label: t("Ascending (Soonest First)") },
    { sortOrder: "desc", label: t("Descending (Furthest First)") },
  ]

  //hooks
  const [searchParams, setSearchParams] = useSearchParams({ ...params })
  const [rowIds, updateRowIds, doesRowIdExist] = useManageExpandedRowIds()
  const userPrivileges = useSelector(state => state.app.privileges)

  //state
  const [addTaskFormProps, setAddTaskFormProps] = useState([])
  const [todo, setTodo] = useState()

  //api_call
  const queryParams = { ...defaultParams, ...Object.fromEntries([...searchParams]) }
  const queryObj = modifyUrlParamsForExistingSearches(queryParams)

  if (queryObj?.activeTab) {
    delete queryObj.activeTab
  }

  const { data, isLoading, isFetching, error, refetch } = useGetTodosQuery(
    { ...queryObj },
    { refetchOnMountOrArgChange: true }
  )

  const [
    getEditedTaskDetails,
    { data: editedTaskData, isFetching: editedTaskDataFetching, isError, refetch: refetchEdited },
  ] = useLazyGetEditedTaskDetailsQuery()

  //useEffects
  useEffect(() => {
    isFetching && selectedTodos.length && setSelectedTodos([])
    handleIsFetching(isFetching)
  }, [isFetching])

  useEffect(() => {
    !!data?.count && getCount(data?.count)
  }, [data])

  //functions
  const onActionClick = (componentRef, todo) => {
    setTodo(todo)
    componentRef?.current?.setVisible(true)
  }

  const rowSelection = useMemo(() => {
    return {
      onChange: rows => {
        const transformData = data?.data?.filter(item => {
          return rows.includes(item.id)
        })
        setSelectedTodos(transformData)
      },
      selectedRowKeys: selectedTodos?.map(item => item.id),
    }
  }, [data, selectedTodos, from])

  const todosColumn = useMemo(() => {
    const columnsToHide = TODOS_CONFIG[page_type]?.columnsToHide || []
    const firstColumnClassName = TODOS_CONFIG[page_type]?.addExtraSpaceToFirstColumn ? styles.tableStartsSpaceExtra : ""

    const allColumns = [
      { title: t("TODO"), dataIndex: "todo", width: "23%" },
      { title: t("LAST TASK"), dataIndex: "lastTask" },
      { title: t("STAFF (ASSIGNEE)"), dataIndex: "staff" },
      { title: t("LEAD ID"), dataIndex: "leadId" },
      { title: t("CLIENT"), dataIndex: "client" },
      { title: t("INTERESTED"), dataIndex: "interest" },
      { title: t("ACTIONS"), dataIndex: "actions", className: "text-right" },
    ]

    //add extra space or any style in future to first column
    allColumns[0].className = firstColumnClassName

    let result = allColumns.filter(column => {
      return !columnsToHide.includes(column.dataIndex)
    })
    return result
  }, [data, from])

  const getActionButtons = todo => {
    const actionsToHide = TODOS_CONFIG[page_type]?.actionsToHide || []
    const allActions = [
      {
        key: "call",
        Component: () => (
          <CallButton
            client={todo?.InternalInquiry?.Client}
            shape="circle"
            disabled={!getPrivilege("can_view_client_contact", todo?.privileges)}
            from={{ interacted_from: PAGE_SUBSECTIONS.todos_listings_page }}
          />
        ),
      },
      ...(configs?.allowGrandStreamCall
        ? [
            {
              key: "grandStream",
              Component: () => (
                <GrandStreamCallButton
                  client={todo?.InternalInquiry?.Client}
                  from={{
                    page: PAGE_SUBSECTIONS.todos_listings_page,
                    interacted_from: PAGE_SUBSECTIONS.todos_listings_page,
                  }}
                  disabled={!getPrivilege("can_view_client_contact", todo?.privileges)}
                />
              ),
            },
          ]
        : []),
      {
        key: "whatsapp",
        Component: () => (
          <WhatsappCallButton
            client={todo?.InternalInquiry?.Client}
            from={{ interacted_from: PAGE_SUBSECTIONS.todos_listings_page }}
            disabled={!getPrivilege("can_view_client_contact", todo?.privileges)}
          />
        ),
      },
      {
        key: "email",
        icon: "HiOutlineMail",
        color: theme["primary-color"],
        content: t("Email"),
        onClick: () => {
          openMailTo(todo?.InternalInquiry?.Client?.email)
        },
        disabled: !getPrivilege("can_send_email", todo?.privileges),
      },
      {
        key: "add_task",
        icon: "MdAddTask",
        color: theme["primary-color"],
        content: t("Add Action"),
        title: t("Select Action Type"),
        iconTextWrapperClass: styles.actionDropdDown,
        options: [
          {
            key: "add_task",
            icon: "MdAddTask",
            color: theme["primary-color"],
            label: t("Add Task"),
            onClick: () => {
              setAddTaskFormProps({})
              onActionClick(addTaskRef, todo)
            },
            disabled: !getPrivilege("can_add_task_on_lead", todo?.privileges),
          },
          {
            key: "add_sales",
            icon: "BsCreditCard",
            color: theme["primary-color"],
            label: t("Add Sale"),
            onClick: () => {
              onActionClick(addSalesFormRef, todo)
            },
            disabled: !getPrivilege("can_see_task_logs", todo?.privileges),
          },
          {
            key: "addToPipeline",
            icon: "BsPersonX",
            color: theme["primary-green-1"],
            label: t("Add to Pipeline"),
            size: "16px",
            onClick: () => {
              onActionClick(addToSalesPipelineRef, todo)
            },
            disabled: !getPrivilege("can_add_to_pipeline", userPrivileges),
          },
          {
            key: "closeLost",
            icon: "BsPersonX",
            color: "#3655b3",
            label: t("Mark Lead as Closed(LOST)"),
            onClick: () => {
              onActionClick(closeLostRef, todo)
            },
          },
        ],
      },
      {
        key: "logs",
        icon: "IoDocumentTextOutline",
        color: theme["primary-color"],
        content: t("Logs"),
        title: t("View & Add"),
        iconTextWrapperClass: styles.actionDropdDown,
        options: [
          {
            key: "tasks",
            icon: "IconTaskList",
            color: "#3655B3",
            label: t("Task Logs"),
            onClick: () => {
              onActionClick(taskLogRef, todo)
            },
            disabled: !getPrivilege("can_see_task_logs", todo?.privileges),
          },
          {
            key: "calls",
            icon: "IconCallLogs",
            color: "#ce7b29",
            size: "1.4em",
            label: t("Call Logs"),
            onClick: () => {
              callDrawerRef?.current?.showDrawer(todo?.InternalInquiry?.Client)
            },
            disabled: !getPrivilege("can_view_call_logs", todo?.privileges),
          },
        ],
      },
    ]
    let actions = allActions.filter(action => {
      return !actionsToHide?.includes(action?.key)
    })
    return actions
  }
  const tableData = useCallback(
    (data, showEditButton) => {
      const tableDataItem = ({ item }) => {
        const canEdit = showEditButton && item?.canEdit && getPrivilege("can_edit_task_on_lead", item?.privileges)

        return item
          ? {
              key: item?.id,
              taskId: item?.taskId,
              todo: (
                <div>
                  <TitleDescription
                    title={
                      <>
                        <Space size={4}>
                          {item?.source === "auto" && <SvgIconAi size="18px" style={{ verticalAlign: -3 }} />}
                          {localisedValue({ key: "nextStatusTitle", object: item }) ||
                            localisedValue({ key: "todoOperationTitle", object: item }) ||
                            "-"}
                          {canEdit && (
                            <EditIconForEditTask
                              item={item}
                              onClick={event => {
                                event.stopPropagation()
                                setTodo(item)
                                setAddTaskFormProps({ isEditable: true })
                                addTaskRef.current?.setVisible(true)
                              }}
                            />
                          )}

                          {showEditButton && getPrivilege("can_see_task_logs", item?.privileges) && item?.editedText ? (
                            <>
                              <EditedTaskTooltip taskId={item?.taskId} from={"todo"} textToShow={item?.editedText} />
                              <EditedTaskShowPreviousButton
                                item={item}
                                showText={t("Show Original")}
                                hideText={t("Hide Original")}
                                doesRowIdExist={doesRowIdExist}
                              />
                            </>
                          ) : null}
                          {item?.source === "auto" && getPrivilege("can_add_feedback", userPrivileges) && (
                            <Button
                              size="small"
                              outlined
                              onClick={event => {
                                setTodo(item)
                                aiFeedbackRef.current.setVisible(true)
                              }}
                              className={cx("showBtn p-0 pi-12", doesRowIdExist(item?.id) && "showAlways")}
                              icon={<Icon icon="MdOutlineRateReview" size="1em" color={theme["gray700"]} />}
                              style={{ "--btn-bg": "transparent" }}
                            />
                          )}
                        </Space>
                      </>
                    }
                    description={
                      <>
                        {item?.nextStatusDeadline ? (
                          <DateFormat
                            color={
                              queryObj?.deadlineFilter === "overdue" || item?.deadlineLabel === "overdue"
                                ? "error-color"
                                : "gray--800"
                            }
                            date={item?.nextStatusDeadline}
                            showTime={true}
                            dateClassName={
                              queryObj?.deadlineFilter === "overdue" || item?.deadlineLabel === "overdue"
                                ? "error-color"
                                : "gray--800"
                            }
                          />
                        ) : (
                          "-"
                        )}
                      </>
                    }
                  />
                </div>
              ),
              lastTask: (
                <TitleDescription
                  title={
                    `${
                      item?.TaskPerformed?.ParentStatus
                        ? `${localisedValue({ key: "name", object: item?.TaskPerformed?.ParentStatus })} - `
                        : ""
                    }${item?.TaskPerformed ? localisedValue({ key: "name", object: item?.TaskPerformed }) : ""}` || ""
                  }
                  description={
                    <Tooltip title={dayjs(item?.updatedAt).format(TIME_DATE_FORMAT)}>
                      {getElapsedTime(item?.updatedAt)}
                    </Tooltip>
                  }
                />
              ),
              staff: item?.Assignee && (
                <div>
                  <div>
                    {localisedValue({ key: "name", object: item?.Assignee })}&nbsp;
                    <UserProfileSummary staffId={item?.assigneeId} />
                  </div>
                  <div className="gray-800 fs14">
                    {joinBytItems(
                      [
                        localisedValue({ key: "short", object: item?.Assignee?.UserDesignation }),
                        localisedValue({ key: "name", object: item?.Assignee?.City }),
                      ],
                      <Separator />
                    )}
                  </div>
                </div>
              ),
              client: (
                <>
                  <Row>
                    {getPrivilege("can_view_client_details", item?.privileges) ? (
                      <Link
                        to={CLIENT_DETAIL_PATH.replace(":clientId", item?.InternalInquiry?.Client?.id)}
                        state={{ hasPath: true }}
                        className="text-link"
                      >
                        {localisedValue({ key: "name", object: item?.InternalInquiry?.Client })}
                      </Link>
                    ) : (
                      <div>{localisedValue({ key: "name", object: item?.InternalInquiry?.Client })}</div>
                    )}
                  </Row>
                  <FLagWithText
                    style={{ gap: "2px" }}
                    title={localisedValue({ key: "name", object: item?.Client?.Country })}
                    countryCode={item?.InternalInquiry?.Client?.Country?.locCode}
                    infoText={
                      <>
                        <Separator />
                        <div className="gray-800 fs14">
                          {`${item?.InternalInquiry?.Client?.inquiryCount} ${
                            item?.InternalInquiry?.Client?.inquiryCount > 1 ? t("Leads") : t("Lead")
                          }`}
                        </div>
                      </>
                    }
                  />
                </>
              ),
              leadId: (
                <>
                  <IconTextDate
                    message={classifications()?.[item?.InternalInquiry?.InternalInquiryClassification?.id]?.name}
                    iconProps={{
                      color: classifications()?.[item?.InternalInquiry?.InternalInquiryClassification?.id]?.style.color,
                      hasBackground: true,
                    }}
                    icon="MdLocalFireDepartment"
                    title={item?.InternalInquiry?.id}
                    date={item?.InternalInquiry?.createdAt}
                    suffixIcon={
                      !!item?.InternalInquiry?.isShared ? (
                        <ViewSharingDetail leadId={item?.InternalInquiry?.id} />
                      ) : null
                    }
                    linkInfo={
                      getPrivilege("can_view_client_details", item?.privileges)
                        ? CLIENT_DETAIL_PATH.replace(":clientId", item?.InternalInquiry?.Client?.id)
                        : false
                    }
                  />
                  <Row align="middle" className="gray-800 fs14 pointer" style={{ gap: 8 }}></Row>
                </>
              ),
              interest:
                item?.InternalInquiry?.interestedIn === "mortgage" ? (
                  <Text>{t("Bank Loan")}</Text>
                ) : 
                 item?.InternalInquiry?.interestedIn === "investment" && !item?.InternalInquiry?.Project?.name ? (
                   <Text>{t("Investment")}</Text>
                 ) :
                (
                  <TitleDescription
                    title={
                      <div className="text-link pointer">
                        {localisedValue({ key: "name", object: item?.InternalInquiry?.Project })}
                      </div>
                    }
                    gapSpace={4}
                    onClick={event => {
                      projectRef.current?.showFor(item?.InternalInquiry?.Project?.projectId)
                      event.stopPropagation()
                    }}
                    description={joinBytItems(
                      [
                        localisedValue({ key: "name", object: item?.InternalInquiry?.Type }),
                        localisedValue({ key: "name", object: item?.InternalInquiry?.Purpose }),
                        capitalizeFirstLetter(localisedValue({ key: "interestedIn", object: item?.InternalInquiry })),
                      ],
                      <Icon icon="GoDotFill" size=".7em" color="#D6D6D6" />
                    )}
                    titleClassName="text-link"
                    key={`interest - ${item?.id}`}
                  />
                ),
              allocatedTo: item?.User && (
                <div>
                  <div>
                    {localisedValue({ key: "name", object: item?.User })}&nbsp;
                    <UserProfileSummary staffId={item?.User?.id} />
                  </div>
                  <div className="gray-800 fs14">
                    {joinBytItems(
                      [
                        localisedValue({ key: "short", object: item?.User?.UserDesignation }),
                        localisedValue({ key: "name", object: item?.User?.City }),
                      ],
                      <Separator />
                    )}
                  </div>
                </div>
              ),
              actions: <ActionButtons list={getActionButtons(item)} />,
            }
          : {}
      }

      return data?.map((item, index) => tableDataItem({ item }))
    },
    [data, editedTaskData, rowIds]
  )

  const onViewTask = task => {
    if (task?.editedText) {
      updateRowIds(task?.id)
      if (!doesRowIdExist(task?.id)) {
        getEditedTaskDetails({ taskId: task?.taskId, detailsType: "todo" }, true)
          .unwrap()
          .then(response => {})
          .catch(error => {
            notification.error({ message: error.message })
          })
      }
    }
    return
  }

  //functions-Tabs
  const todoCategories = useMemo(
    () =>
      data &&
      Object.keys(data?.deadlineCounters)?.map(todoCategory => {
        return {
          tab: (
            <>
              {convertToReadableString(t(todoCategory), true)}{" "}
              <Text type="secondaryLight">
                <Number
                  wrapValue={val => `(${val})`}
                  value={data?.deadlineCounters?.[todoCategory] || 0}
                  defaultValue="0"
                  digitAfterFraction={1}
                  isStringOnly
                  tooltip
                />
              </Text>
            </>
          ),
          key: todoCategory,
        }
      }),
    [data, t]
  )

  const onTabChange = newTab => {
    const obj = { ...queryObj }
    obj["activeTab"] = newTab
    obj["deadlineFilter"] = newTab
    setSearchParams({ ...obj, page: 0 })
  }

  const currentSort = useMemo(() => items?.find(item => item?.sortOrder === queryObj?.sortOrder), [queryObj])

  const onSort = useCallback(
    sortObj => {
      setSearchParams({ ...queryObj, sortOrder: sortObj.sortOrder, page: 0 })
    },
    [queryObj, searchParams]
  )

  const onClickSortItem = item => {
    if (queryObj?.sortOrder !== item?.sortOrder) {
      onSort(item)
    }
  }

  //render
  return (
    <>
      {TODOS_CONFIG[page_type]?.showFilters && <TodosFilters isFetchingTodos={isFetching} filtersRef={filtersRef} />}
      <Card
        tabList={!removeTabs && todoCategories}
        activeTabKey={queryParams?.activeTab || todoCategories?.[0]?.key}
        onTabChange={e => onTabChange(e)}
        style={{ flexGrow: 1 }}
        styles={{
          // header: { position: "sticky", top: 64, zIndex: 5 },
          body: { padding: !removeTabs ? "25px 0 0" : "0" },
        }}
        tabBarExtraContent={<Sort options={items} sort={currentSort} onClickItem={onClickSortItem} />}
      >
        <DataTable
          tableLayout="auto"
          pagination={{ totalCount: data?.count, pageOptions: [10, 25, 50, 300] }}
          columns={todosColumn}
          data={tableData(data?.data, true) || []}
          loading={isFetching}
          // stickyOffset={50}
          noUrlPush={false}
          error={error}
          // sticky
          onErrorRetry={refetch}
          skeletonLoading={isLoading}
          expandedRowKeys={rowIds}
          onRow={(record, rowIndex) => {
            return {
              onClick: _event => {
                onViewTask(data?.data[rowIndex])
              },
            }
          }}
          skeletonType ={ TODOS_CONFIG[page_type]?.skeletonType}
          expandable={{
            expandRowByClick: true,
            expandIcon: () => null,
            expandedRowClassName: () => "expandedTableOuter",
            expandedRowRender: (_record, index) => (
              <DataTable
                mainClass={styles.expandedTable}
                columns={todosColumn}
                data={tableData(editedTaskData) || []}
                loading={editedTaskDataFetching}
                onErrorRetry={refetchEdited}
                error={isError}
                skeletonRowCount={1}
              />
            ),
            showExpandColumn: false,
          }}
          rowClassName={(_record, index) =>
            !!doesRowIdExist(data?.data?.[index]?.id) ? cx(styles.rowView, styles.expandedParentRow) : styles.rowView
          }
          rowSelection={showRowSelection ? { columnWidth: 70, ...rowSelection } : null}
          {...extraProps}
        />
      </Card>

      <ProjectModal ref={projectRef} />
      <AddSalesContextProvider>
        <AddSalesModal lead={todo?.InternalInquiry} client={todo?.InternalInquiry?.Client} formRef={addSalesFormRef} />
      </AddSalesContextProvider>
      <AddTaskForm
        lead={{ ...todo?.InternalInquiry, clientId: todo?.InternalInquiry?.Client?.id }}
        client={todo?.InternalInquiry?.Client}
        formRef={addTaskRef}
        {...addTaskFormProps}
      />
      <TaskOverviewModal
        client={todo?.Client}
        lead={{ id: todo?.id }}
        ref={taskLogRef}
        taskTableProps={{ defaultFilters: { inquiryIds: todo?.id?.toString() } }}
      />
      <TaskOverviewModal
        client={todo?.InternalInquiry?.Client}
        lead={{ id: todo?.InternalInquiry?.id }}
        ref={taskLogRef}
        taskTableProps={{
          from: "",
          lead: { inquiryIds: todo?.InternalInquiry?.id.toString() },
          isEditable: true,
        }}
        getClientLogs
      />
      <CloseLostForm formRef={closeLostRef} lead={{ id: todo?.InternalInquiry?.id }} />
      <CallLogsDrawer ref={callDrawerRef} />

      <AddToSalesPipeline
        ref={addToSalesPipelineRef}
        lead={todo?.InternalInquiry}
        client={todo?.InternalInquiry?.Client}
      />
      <AiFeedbackForm ref={aiFeedbackRef} entityId={todo?.id} entity={"task_log"} />
    </>
  )
}
